import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { currency } from "@/lib/constants";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from "recharts";
import { X, Send, ExternalLink, MessageSquare, Flag, Maximize2, Filter as FilterIcon, RotateCcw, Bookmark, BookmarkPlus, Trash2 } from "lucide-react";

const STAGE_FILTER_OPTIONS = [
  { value: "", label: "All stages" },
  { value: "draft", label: "Draft" },
  { value: "submitted", label: "Submitted" },
  { value: "in_review", label: "In Review" },
  { value: "attorney_review", label: "Attorney Review" },
  { value: "gc_review", label: "GC Review" },
  { value: "approved", label: "Approved" },
  { value: "executed", label: "Executed" },
  { value: "expired", label: "Expired" },
  { value: "terminated", label: "Terminated" },
];

const CONTRACT_TYPE_OPTIONS = [
  { value: "", label: "All types" },
  { value: "Services", label: "Services" },
  { value: "Software", label: "Software" },
  { value: "Goods", label: "Goods" },
  { value: "Consulting", label: "Consulting" },
  { value: "Lease", label: "Lease" },
  { value: "MOU", label: "MOU" },
  { value: "NDA", label: "NDA" },
  { value: "Other", label: "Other" },
];

const RED_FLAG_TYPE_OPTIONS = [
  { value: "", label: "Any / no filter" },
  { value: "arbitration", label: "Arbitration Clause" },
  { value: "auto_renewal", label: "Auto-Renewal" },
  { value: "cpi_escalator", label: "CPI / Price Escalator" },
  { value: "termination_restriction", label: "Termination Restrictions" },
  { value: "indemnification", label: "Indemnification / Liability" },
  { value: "insurance_gap", label: "Insurance Gaps" },
  { value: "phi_baa", label: "Data / PHI / BAA" },
  { value: "non_standard_payment", label: "Non-Standard Payment" },
  { value: "custom", label: "Other (Custom)" },
];

const COLORS = ["#005289", "#00AE42", "#F59E0B", "#EF4444", "#8B5CF6", "#0891B2", "#DB2777", "#65A30D", "#7C3AED"];

function Card({ title, children, sub, actions }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md">
      <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
        <div>
          <div className="text-sm font-bold text-slate-900">{title}</div>
          {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
        </div>
        {actions}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function DrillModal({ open, onClose, title, rows, onOpenContract, onMessageManager }) {
  const [selected, setSelected] = useState({});
  const [msgOpen, setMsgOpen] = useState(false);
  const [msg, setMsg] = useState("");
  useEffect(() => { if (open) { setSelected({}); setMsgOpen(false); setMsg(""); } }, [open]);
  if (!open) return null;
  const selectedIds = Object.keys(selected).filter(k => selected[k]);
  const send = async () => {
    if (!msg.trim()) return toast.error("Enter a message for the manager");
    try {
      await onMessageManager({ body: msg.trim(), contract_ids: selectedIds, context: title });
      toast.success("Message sent to the Director of Legal Team");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" data-testid="drill-modal">
      <div className="bg-white rounded-md max-w-4xl w-full max-h-[85vh] flex flex-col shadow-xl">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-sm font-bold text-slate-900">{title}</div>
            <div className="text-[11px] text-slate-500">{rows.length} contract{rows.length===1?"":"s"} · Select rows to reference in a manager message</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="drill-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-auto p-4">
          {rows.length === 0 && <div className="text-center py-8 text-sm text-slate-500">Nothing to show.</div>}
          {rows.length > 0 && (
            <table className="hcd-table">
              <thead>
                <tr>
                  <th className="w-8"><input type="checkbox" checked={selectedIds.length === rows.length && rows.length > 0} onChange={e => {
                    const all = {}; if (e.target.checked) rows.forEach(r => { all[r.id || r.contract_id] = true; });
                    setSelected(all);
                  }} data-testid="drill-select-all" /></th>
                  <th>#</th><th>Vendor</th><th>Stage / Dept</th><th className="num">Metric</th><th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const rid = r.id || r.contract_id;
                  return (
                    <tr key={rid}>
                      <td><input type="checkbox" checked={!!selected[rid]} onChange={e => setSelected({...selected, [rid]: e.target.checked})} data-testid={`drill-check-${r.contract_number}`} /></td>
                      <td className="font-mono text-xs">{r.contract_number}</td>
                      <td className="font-semibold">{r.vendor}</td>
                      <td className="text-xs text-slate-600">{r.stage || r.department || "—"}</td>
                      <td className="num text-xs">
                        {r.days_over != null && <span className="text-red-600 font-bold">{r.days_over}d over</span>}
                        {r.days_left != null && <span className="text-amber-600 font-bold">{r.days_left}d left</span>}
                        {r.days_until != null && <span className="text-[#005289] font-bold">{r.days_until}d</span>}
                      </td>
                      <td>
                        <button onClick={() => onOpenContract(rid)} className="text-xs text-[#005289] hover:underline flex items-center gap-1" data-testid={`drill-open-${r.contract_number}`}>
                          Open <ExternalLink className="w-3 h-3" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 space-y-2">
          {!msgOpen && (
            <div className="flex items-center justify-between">
              <div className="text-[11px] text-slate-500">{selectedIds.length} selected</div>
              <button onClick={() => setMsgOpen(true)} className="h-8 px-3 rounded-md text-white text-xs font-semibold flex items-center gap-1.5"
                      style={{ background: "#005289" }} data-testid="drill-open-msg">
                <MessageSquare className="w-3.5 h-3.5" /> Message Manager
              </button>
            </div>
          )}
          {msgOpen && (
            <div className="space-y-2">
              <textarea value={msg} onChange={e => setMsg(e.target.value)}
                        placeholder={`Message to the Director of Legal Team about "${title}"...`}
                        className="w-full min-h-[70px] p-2 text-sm border border-slate-200 rounded" data-testid="drill-msg" />
              <div className="flex justify-end gap-2">
                <button onClick={() => setMsgOpen(false)} className="h-8 px-3 text-xs font-semibold border border-slate-200 rounded">Cancel</button>
                <button onClick={send} className="h-8 px-3 rounded-md text-white text-xs font-semibold flex items-center gap-1.5"
                        style={{ background: "#00AE42" }} data-testid="drill-send-msg">
                  <Send className="w-3.5 h-3.5" /> Send to Manager
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const EMPTY_FILTERS = { stage: "", department: "", contract_type: "", red_flag_type: "", compliance_cleared: "", executive_sponsor: "", bu_approver: "", from_date: "", to_date: "", search: "" };

export default function Reports() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isDirector = user?.role === "department_director";
  const [summary, setSummary] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [horizon, setHorizon] = useState(5);
  const [drill, setDrill] = useState({ open: false, title: "", rows: [] });
  const [priorities, setPriorities] = useState([]);
  const [priorityFeed, setPriorityFeed] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [loading, setLoading] = useState(false);
  const [savedViews, setSavedViews] = useState([]);
  const [activeViewId, setActiveViewId] = useState(null);
  const [trendMonth, setTrendMonth] = useState(null);
  const [trendRows, setTrendRows] = useState(null);
  const [digestEmails, setDigestEmails] = useState([]);
  const [digestOpen, setDigestOpen] = useState(false);
  const isGC = user?.role === "general_counsel" || user?.role === "it_admin";

  const loadSavedViews = () => {
    api.get("/saved-views?scope=reports")
      .then(r => setSavedViews(r.data || []))
      .catch(() => {});
  };
  useEffect(() => { loadSavedViews(); }, []);

  useEffect(() => {
    const handler = (e) => {
      const m = e?.detail?.month;
      if (!m) return;
      setTrendMonth(m); setTrendRows(null);
      api.get(`/reports/cleared-in-month?month=${m}`).then(r => setTrendRows(r.data || [])).catch(() => setTrendRows([]));
    };
    window.addEventListener("reports:trend:click", handler);
    return () => window.removeEventListener("reports:trend:click", handler);
  }, []);

  useEffect(() => {
    if (!isGC) return;
    api.get("/reports/digest-recipients").then(r => setDigestEmails(r.data.emails || [])).catch(() => {});
  }, [isGC]);

  const saveDigestRecipients = async (emails) => {
    try {
      const r = await api.post("/reports/digest-recipients", { emails });
      setDigestEmails(r.data.emails || []);
      toast.success("Digest recipients updated");
    } catch (e) { toast.error("Update failed"); }
  };

  const applyView = (v) => {
    const { scope, is_default, ...rest } = (v.filters || {});
    setFilters({ ...EMPTY_FILTERS, ...rest });
    setActiveViewId(v.id);
  };
  const saveCurrentView = async () => {
    const name = window.prompt("Name this view (e.g., 'Clinical execs this quarter'):");
    if (!name || !name.trim()) return;
    try {
      const { data } = await api.post("/saved-views", {
        name: name.trim(),
        filters: { ...filters, scope: "reports" },
      });
      setSavedViews(v => [data, ...v]);
      setActiveViewId(data.id);
      toast.success(`Saved "${data.name}"`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const deleteView = async (v, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete saved view "${v.name}"?`)) return;
    try {
      await api.delete(`/saved-views/${v.id}`);
      setSavedViews(list => list.filter(x => x.id !== v.id));
      if (activeViewId === v.id) setActiveViewId(null);
      toast.success("Deleted");
    } catch (e2) { toast.error("Delete failed"); }
  };

  const filterQS = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
    const qs = p.toString();
    return qs ? `?${qs}` : "";
  }, [filters]);

  useEffect(() => {
    setLoading(true);
    api.get(`/reports/summary${filterQS}`)
      .then(r => setSummary(r.data))
      .catch(() => toast.error("Failed to load summary"))
      .finally(() => setLoading(false));
  }, [filterQS]);
  useEffect(() => { api.get(`/reports/cost-forecast?years=${horizon}`).then(r => setForecast(r.data)).catch(() => {}); }, [horizon]);
  useEffect(() => {
    api.get("/reports/priority").then(r => setPriorities(r.data || [])).catch(() => {});
    api.get("/reports/priority-updates?limit=15").then(r => setPriorityFeed(r.data || [])).catch(() => {});
  }, []);

  if (!summary) return <div className="p-8 text-slate-500">Loading reports...</div>;

  const byDept = [...(summary.by_department || [])].sort((a, b) => b.annual - a.annual);
  const byStage = [...(summary.by_stage || [])].sort((a, b) => b.count - a.count);
  const byType = [...(summary.by_type || [])].sort((a, b) => b.count - a.count);
  const monthly = summary.monthly_intake || [];

  const departmentOptions = [...new Set(byDept.map(d => d.name))].sort();

  const updateFilter = (key, val) => { setFilters(f => ({ ...f, [key]: val })); setActiveViewId(null); };
  const resetFilters = () => { setFilters(EMPTY_FILTERS); setActiveViewId(null); };
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const exportSummary = () => {
    const csv = ["Department,Contracts,Annual"];
    byDept.forEach(d => csv.push(`${d.name},${d.count},${d.annual}`));
    const blob = new Blob([csv.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = "financial_by_dept.csv"; a.click();
  };

  const messageManager = ({ body, contract_ids, context }) =>
    api.post("/reports/message-manager", { body, contract_ids, context });

  return (
    <div className="p-6 space-y-6" data-testid="reports-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Reports & Business Intelligence</h1>
          <p className="text-sm text-slate-500 mt-1">
            Financial roll-ups, cycle-time analytics, renewals and operational reports.
            {isDirector && <span className="ml-1 text-[#005289] font-semibold">Scoped to your department.</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isGC && (
            <button onClick={() => setDigestOpen(true)}
                    className="h-9 px-3 border border-slate-200 rounded-md text-xs font-semibold hover:bg-slate-50"
                    data-testid="digest-recipients-btn">
              Digest Recipients ({digestEmails.length})
            </button>
          )}
          <button onClick={exportSummary} className="h-9 px-3 border border-slate-200 rounded-md text-xs font-semibold hover:bg-slate-50" data-testid="export-financial">Export Financials CSV</button>
        </div>
      </div>

      {trendMonth && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" data-testid="reports-trend-drill">
          <div className="bg-white rounded-md max-w-3xl w-full max-h-[85vh] flex flex-col shadow-xl">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="text-sm font-bold text-slate-900">Contracts Cleared in {trendMonth}</div>
                <div className="text-[11px] text-slate-500">{trendRows ? `${trendRows.length} contract${trendRows.length===1?"":"s"}` : "Loading…"}</div>
              </div>
              <button onClick={() => setTrendMonth(null)} className="text-slate-400 hover:text-slate-700" data-testid="reports-trend-drill-close"><X className="w-4 h-4" /></button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              {!trendRows && <div className="text-sm text-slate-500 py-6 text-center">Loading…</div>}
              {trendRows && trendRows.length === 0 && <div className="text-sm text-slate-500 py-6 text-center">No contracts cleared this month.</div>}
              {trendRows && trendRows.length > 0 && (
                <table className="hcd-table">
                  <thead><tr><th>#</th><th>Vendor</th><th>Dept</th><th className="num">Annual</th><th>Cleared by</th><th>Cleared at</th></tr></thead>
                  <tbody>
                    {trendRows.map(r => (
                      <tr key={r.id} onClick={() => nav(`/contract/${r.id}`)} className="cursor-pointer hover:bg-slate-50">
                        <td className="font-mono text-xs">{r.contract_number}</td>
                        <td className="font-semibold">{r.vendor_legal_name}</td>
                        <td className="text-xs">{r.department}</td>
                        <td className="num">{r.contract_amount ? `$${r.contract_amount.toLocaleString()}` : "—"}</td>
                        <td className="text-xs">{r.cleared_by_name}</td>
                        <td className="text-xs text-slate-500">{r.cleared_at ? new Date(r.cleared_at).toLocaleDateString() : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {digestOpen && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" data-testid="digest-recipients-modal">
          <div className="bg-white rounded-md max-w-lg w-full shadow-xl">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <div className="text-sm font-bold text-slate-900">Weekly Compliance Digest Recipients</div>
              <button onClick={() => setDigestOpen(false)} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-[11px] text-slate-500">All active Compliance users automatically receive the digest. Add extra emails (e.g., CFO, external counsel) below.</p>
              <div className="space-y-1.5">
                {digestEmails.map((e, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <span className="flex-1 font-mono text-xs">{e}</span>
                    <button onClick={() => saveDigestRecipients(digestEmails.filter((_, j) => j !== i))}
                            className="text-xs text-red-600 hover:underline" data-testid={`digest-remove-${i}`}>Remove</button>
                  </div>
                ))}
                {digestEmails.length === 0 && <div className="text-xs text-slate-400 italic">No extras yet.</div>}
              </div>
              <form onSubmit={(e) => { e.preventDefault(); const email = e.target.email.value.trim(); if (email) { saveDigestRecipients([...digestEmails, email]); e.target.reset(); } }}
                    className="flex items-center gap-2 pt-2 border-t border-slate-100">
                <input name="email" type="email" required placeholder="cfo@hcdpbc.org" data-testid="digest-add-input"
                       className="flex-1 h-8 px-2 text-sm border border-slate-200 rounded" />
                <button type="submit" className="h-8 px-3 bg-[#005289] text-white rounded text-xs font-semibold" data-testid="digest-add-btn">Add</button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Reports Filters panel */}
      <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="reports-filters">
        <div className="flex items-center gap-2 mb-3">
          <FilterIcon className="w-4 h-4 text-[#005289]" />
          <div className="text-xs font-bold uppercase tracking-wider text-slate-700">Filters</div>
          {activeFilterCount > 0 && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#005289] text-white font-semibold" data-testid="reports-filter-count">{activeFilterCount} active</span>
          )}
          {loading && <span className="text-[10px] text-slate-400 ml-1">Loading…</span>}
          <button onClick={saveCurrentView} disabled={activeFilterCount === 0}
                  className="ml-auto h-7 px-2 text-[11px] font-semibold border border-slate-200 rounded flex items-center gap-1 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="reports-save-view">
            <BookmarkPlus className="w-3 h-3" /> Save view
          </button>
          <button onClick={resetFilters} className="h-7 px-2 text-[11px] font-semibold border border-slate-200 rounded flex items-center gap-1 hover:bg-slate-50" data-testid="reports-filter-reset">
            <RotateCcw className="w-3 h-3" /> Reset
          </button>
        </div>

        {savedViews.length > 0 && (
          <div className="mb-3 flex items-start gap-2 flex-wrap" data-testid="reports-saved-views">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mt-1">Saved</div>
            {savedViews.map(v => {
              const active = activeViewId === v.id;
              return (
                <button key={v.id} onClick={() => applyView(v)} data-testid={`reports-view-${v.id}`}
                        className={`group h-7 pl-2 pr-1 rounded-full text-[11px] font-semibold border flex items-center gap-1 ${active ? "bg-[#005289] text-white border-[#005289]" : "bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100"}`}>
                  <Bookmark className="w-3 h-3" />
                  <span className="max-w-[160px] truncate">{v.name}</span>
                  <span onClick={(e) => deleteView(v, e)} title="Delete" data-testid={`reports-view-delete-${v.id}`}
                        className={`ml-1 w-5 h-5 rounded-full flex items-center justify-center ${active ? "hover:bg-white/20" : "hover:bg-slate-200 text-slate-400"}`}>
                    <Trash2 className="w-3 h-3" />
                  </span>
                </button>
              );
            })}
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <label className="text-[11px] font-semibold text-slate-600 block">
            <span className="block mb-1">Stage</span>
            <select value={filters.stage} onChange={e => updateFilter("stage", e.target.value)} data-testid="reports-filter-stage"
                    className="w-full h-8 px-2 text-xs border border-slate-200 rounded-md bg-white text-slate-700">
              {STAGE_FILTER_OPTIONS.map(o => <option key={o.value || "all"} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          {!isDirector && (
            <label className="text-[11px] font-semibold text-slate-600 block">
              <span className="block mb-1">Department</span>
              <select value={filters.department} onChange={e => updateFilter("department", e.target.value)} data-testid="reports-filter-department"
                      className="w-full h-8 px-2 text-xs border border-slate-200 rounded-md bg-white text-slate-700">
                <option value="">All departments</option>
                {departmentOptions.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </label>
          )}
          <label className="text-[11px] font-semibold text-slate-600 block">
            <span className="block mb-1">Contract Type</span>
            <select value={filters.contract_type} onChange={e => updateFilter("contract_type", e.target.value)} data-testid="reports-filter-type"
                    className="w-full h-8 px-2 text-xs border border-slate-200 rounded-md bg-white text-slate-700">
              {CONTRACT_TYPE_OPTIONS.map(o => <option key={o.value || "all"} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <label className="text-[11px] font-semibold text-slate-600 block">
            <span className="block mb-1">Red-Flag Type</span>
            <select value={filters.red_flag_type} onChange={e => updateFilter("red_flag_type", e.target.value)} data-testid="reports-filter-redflag"
                    className="w-full h-8 px-2 text-xs border border-slate-200 rounded-md bg-white text-slate-700">
              {RED_FLAG_TYPE_OPTIONS.map(o => <option key={o.value || "all"} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <label className="text-[11px] font-semibold text-slate-600 block">
            <span className="block mb-1">Compliance Cleared</span>
            <select value={filters.compliance_cleared} onChange={e => updateFilter("compliance_cleared", e.target.value)} data-testid="reports-filter-cleared"
                    className="w-full h-8 px-2 text-xs border border-slate-200 rounded-md bg-white text-slate-700">
              <option value="">Any status</option>
              <option value="yes">Cleared</option>
              <option value="no">Pending / Not cleared</option>
            </select>
          </label>
          <label className="text-[11px] font-semibold text-slate-600 block">
            <span className="block mb-1">Executive Sponsor</span>
            <select value={filters.executive_sponsor} onChange={e => updateFilter("executive_sponsor", e.target.value)} data-testid="reports-filter-sponsor"
                    className="w-full h-8 px-2 text-xs border border-slate-200 rounded-md bg-white text-slate-700">
              <option value="">All sponsors</option>
              {(summary.by_executive_sponsor || []).map(s => <option key={s.name} value={s.name}>{s.name} ({s.count})</option>)}
            </select>
          </label>
          <label className="text-[11px] font-semibold text-slate-600 block">
            <span className="block mb-1">BU Approver</span>
            <select value={filters.bu_approver} onChange={e => updateFilter("bu_approver", e.target.value)} data-testid="reports-filter-approver"
                    className="w-full h-8 px-2 text-xs border border-slate-200 rounded-md bg-white text-slate-700">
              <option value="">All approvers</option>
              {(summary.by_bu_approver || []).map(s => <option key={s.name} value={s.name}>{s.name} ({s.count})</option>)}
            </select>
          </label>
          <label className="text-[11px] font-semibold text-slate-600 block">
            <span className="block mb-1">From</span>
            <input type="date" value={filters.from_date} onChange={e => updateFilter("from_date", e.target.value)} data-testid="reports-filter-from"
                   className="w-full h-8 px-2 text-xs border border-slate-200 rounded-md bg-white text-slate-700" />
          </label>
          <label className="text-[11px] font-semibold text-slate-600 block">
            <span className="block mb-1">To</span>
            <input type="date" value={filters.to_date} onChange={e => updateFilter("to_date", e.target.value)} data-testid="reports-filter-to"
                   className="w-full h-8 px-2 text-xs border border-slate-200 rounded-md bg-white text-slate-700" />
          </label>
          <label className="text-[11px] font-semibold text-slate-600 block">
            <span className="block mb-1">Search</span>
            <input type="text" value={filters.search} onChange={e => updateFilter("search", e.target.value)} data-testid="reports-filter-search"
                   placeholder="Vendor / number / description"
                   className="w-full h-8 px-2 text-xs border border-slate-200 rounded-md bg-white text-slate-700" />
          </label>
        </div>
        <div className="mt-3 text-[11px] text-slate-500" data-testid="reports-filter-matches">
          Matching contracts: <span className="font-bold text-slate-900">{summary.total}</span>
          {" · "}Active: <span className="font-bold text-slate-900">{summary.active}</span>
          {" · "}Executed: <span className="font-bold text-slate-900">{summary.executed}</span>
        </div>
      </div>

      {/* Cost Forecast (CPI + operating) */}
      {forecast && (
        <Card title={`Multi-Year Cost Forecast (${forecast.years}yr, CPI-escalated)`} sub={`Organization total over ${forecast.years}yr horizon: ${currency(forecast.org_total)}`}>
          <div className="mb-3 flex items-center gap-2">
            <span className="text-xs text-slate-500">Horizon:</span>
            {[1, 2, 3, 4, 5].map(y => (
              <button key={y} onClick={() => setHorizon(y)}
                      className={`px-3 h-7 text-xs font-semibold rounded ${horizon === y ? "bg-[#005289] text-white" : "border border-slate-200 hover:bg-slate-50"}`}
                      data-testid={`horizon-${y}`}>{y}yr</button>
            ))}
          </div>
          <div className="overflow-x-auto">
            <table className="hcd-table">
              <thead>
                <tr>
                  <th>Department</th>
                  <th className="num">Contract $</th>
                  <th className="num">Support $</th>
                  <th className="num">Operating $</th>
                  {forecast.org_yearly.map((_, i) => <th key={i} className="num">Yr {i+1}</th>)}
                  <th className="num">Total {forecast.years}yr</th>
                </tr>
              </thead>
              <tbody>
                {forecast.by_department.map(d => (
                  <tr key={d.department}>
                    <td className="font-semibold">{d.department}</td>
                    <td className="num">{currency(d.contract_amount)}</td>
                    <td className="num">{currency(d.annual_support)}</td>
                    <td className="num">{currency(d.annual_operating)}</td>
                    {d.yearly.map((v, i) => <td key={i} className="num">{currency(v)}</td>)}
                    <td className="num font-bold">{currency(d.total_over_horizon)}</td>
                  </tr>
                ))}
                <tr className="bg-slate-50 font-bold">
                  <td>Organization Total</td>
                  <td className="num" colSpan={3}></td>
                  {forecast.org_yearly.map((v, i) => <td key={i} className="num">{currency(v)}</td>)}
                  <td className="num">{currency(forecast.org_total)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="mt-2 text-[11px] text-slate-500">Forecast applies each contract's effective CPI (capped) to Contract + Support + Operating baseline.</div>
        </Card>
      )}

      {/* Compliance Cleared trend chart */}
      <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="reports-cleared-trend-card">
        <div className="flex items-center gap-2 mb-3">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-700">Compliance Cleared Trend</div>
          <div className="text-[11px] text-slate-500">
            {summary.executed_cleared}/{summary.executed} executed today · {summary.executed_cleared_pct}% cleared
          </div>
        </div>
        {(summary.cleared_trend || []).length > 1 ? (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={summary.cleared_trend} margin={{ top: 6, right: 20, left: 0, bottom: 6 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#64748B" }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "#64748B" }} tickFormatter={v => `${v}%`} />
              <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v, k) => k === "pct" ? [`${v}%`, "Cleared %"] : [v, k]} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="pct" stroke="#00AE42" strokeWidth={2} dot={{ r: 4, cursor: "pointer",
                    onClick: (d) => window.dispatchEvent(new CustomEvent("reports:trend:click", { detail: d.payload })) }}
                    name="Cleared %" />
            </LineChart>
          </ResponsiveContainer>
        ) : <div className="text-sm text-slate-500 py-6 text-center">Not enough data yet.</div>}
        <div className="mt-2 text-[10px] text-slate-400">Click any month dot to see which contracts were cleared that month.</div>
      </div>

      {/* Enterprise "Contracts By" — submitter / cost bucket / length / executive sponsor */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Contracts by Submitter" sub={`Top ${(summary.by_submitter || []).length} by volume`}>
          {(summary.by_submitter || []).length === 0 ? <div className="text-sm text-slate-500 py-6 text-center">No data yet.</div> : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={summary.by_submitter} layout="vertical" margin={{ top: 6, right: 20, left: 100, bottom: 6 }} data-testid="reports-by-submitter">
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10, fill: "#64748B" }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: "#334155" }} width={120} />
                <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v, k) => k === "annual" ? currency(v) : v} />
                <Bar dataKey="count" fill="#005289" radius={[0,3,3,0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
        <Card title="Contracts by Cost Bucket" sub="Distribution across value bands">
          {(summary.by_cost_bucket || []).length === 0 ? <div className="text-sm text-slate-500 py-6 text-center">No data.</div> : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={summary.by_cost_bucket} margin={{ top: 10, right: 20, left: 0, bottom: 20 }} data-testid="reports-by-cost">
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748B" }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#64748B" }} />
                <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v, k) => k === "annual" ? currency(v) : v} />
                <Bar dataKey="count" fill="#00AE42" radius={[3,3,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
        <Card title="Contracts by Term Length" sub="1yr / 3yr / 5yr+ mix">
          {(summary.by_term_length || []).length === 0 ? <div className="text-sm text-slate-500 py-6 text-center">No data.</div> : (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart data-testid="reports-by-length">
                <Pie data={summary.by_term_length} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={95} label={(e) => `${e.name}: ${e.count}`}>
                  {(summary.by_term_length || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>
        <Card title="Contracts by Executive Sponsor" sub={(summary.by_executive_sponsor || []).length ? `Top ${(summary.by_executive_sponsor || []).length}` : "Add sponsors in Intake to populate this chart"}>
          {(summary.by_executive_sponsor || []).length === 0 ? (
            <div className="text-xs text-slate-500 py-8 text-center italic">
              No executive sponsors captured yet.<br/>Add one on the Intake form — it appears here automatically.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={summary.by_executive_sponsor} layout="vertical" margin={{ top: 6, right: 20, left: 100, bottom: 6 }} data-testid="reports-by-exec-sponsor">
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10, fill: "#64748B" }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: "#334155" }} width={120} />
                <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v, k) => k === "annual" ? currency(v) : v} />
                <Bar dataKey="count" fill="#7C3AED" radius={[0,3,3,0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      {/* Financial roll-ups */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card title="Annual Contract Value by Department" sub={`Organization total: ${currency(summary.total_annual)}`}>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={byDept} margin={{ top: 10, right: 20, left: 0, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748B" }} interval={0} angle={-25} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 10, fill: "#64748B" }} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => currency(v)} contentStyle={{ fontSize: 12 }} />
                <Bar dataKey="annual" fill="#005289" radius={[3,3,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </div>
        <Card title="Contracts by Department" sub={`${summary.total} total`}>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={byDept} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(e) => e.count}>
                {byDept.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Stage funnel + Type breakdown + Monthly intake */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card title="Contracts by Stage" sub="Funnel across the pipeline">
          {byStage.length === 0 ? <div className="text-sm text-slate-500 py-8 text-center">No data.</div> : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={byStage} margin={{ top: 10, right: 20, left: 0, bottom: 40 }} data-testid="reports-by-stage">
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748B" }} interval={0} angle={-25} textAnchor="end" height={60}
                       tickFormatter={v => (v || "").replace(/_/g, " ")} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#64748B" }} />
                <Tooltip contentStyle={{ fontSize: 12 }} labelFormatter={(v) => (v || "").replace(/_/g, " ")} />
                <Bar dataKey="count" fill="#00AE42" radius={[3,3,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
        <Card title="Contract Type Mix" sub="Distribution of matching contracts">
          {byType.length === 0 ? <div className="text-sm text-slate-500 py-8 text-center">No data.</div> : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart data-testid="reports-by-type">
                <Pie data={byType} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(e) => `${e.name} (${e.count})`}>
                  {byType.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>
        <Card title="Monthly Intake (last 12 mo.)" sub="New contracts created per month">
          {monthly.length === 0 ? <div className="text-sm text-slate-500 py-8 text-center">No data.</div> : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={monthly} margin={{ top: 10, right: 20, left: 0, bottom: 30 }} data-testid="reports-monthly-intake">
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#64748B" }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#64748B" }} />
                <Tooltip contentStyle={{ fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="count" stroke="#005289" strokeWidth={2} dot={{ r: 3 }} name="Contracts" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      {/* Roll-up table */}
      <Card title="Department Roll-up">
        <div className="overflow-x-auto">
          <table className="hcd-table">
            <thead><tr><th>Department</th><th className="num">Contracts</th><th className="num">Annual Value</th><th className="num">Avg / Contract</th></tr></thead>
            <tbody>
              {byDept.map(d => (
                <tr key={d.name}>
                  <td className="font-semibold">{d.name}</td>
                  <td className="num font-mono">{d.count}</td>
                  <td className="num">{currency(d.annual)}</td>
                  <td className="num text-slate-500">{currency(d.count ? d.annual / d.count : 0)}</td>
                </tr>
              ))}
              <tr className="bg-slate-50 font-bold">
                <td>Total</td>
                <td className="num font-mono">{summary.total}</td>
                <td className="num">{currency(summary.total_annual)}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      {/* Operational — Drill-down enabled */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Overdue Contracts" sub={`${summary.overdue.length} contract${summary.overdue.length===1?"":"s"} past SLA`}
              actions={summary.overdue.length > 0 && (
                <button onClick={() => setDrill({ open: true, title: "Overdue Contracts", rows: summary.overdue })}
                        title="Pop out & message manager"
                        aria-label="Pop out overdue contracts and message manager"
                        className="w-8 h-8 rounded-md text-white inline-flex items-center justify-center hover:opacity-90"
                        style={{ background: "#EF4444" }}
                        data-testid="drill-overdue">
                  <Maximize2 className="w-4 h-4" />
                </button>
              )}>
          {summary.overdue.length === 0 ? <div className="text-sm text-slate-500 py-6 text-center">Nothing overdue.</div> : (
            <table className="hcd-table"><thead><tr><th>#</th><th>Vendor</th><th>Stage</th><th className="num">Days over</th></tr></thead>
              <tbody>
                {summary.overdue.slice(0, 8).map((o, i) => (
                  <tr key={i} onClick={() => nav(`/contract/${o.id}`)} className="cursor-pointer hover:bg-slate-50">
                    <td className="font-mono text-xs">{o.contract_number}</td>
                    <td className="font-semibold">{o.vendor}</td>
                    <td>{o.stage}</td>
                    <td className="num text-red-600 font-bold">{o.days_over}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
        <Card title="Due Soon" sub={`${summary.due_soon.length} contract${summary.due_soon.length===1?"":"s"} within 5 days`}
              actions={summary.due_soon.length > 0 && (
                <button onClick={() => setDrill({ open: true, title: "Due Soon", rows: summary.due_soon })}
                        title="Pop out & message manager"
                        aria-label="Pop out due-soon contracts and message manager"
                        className="w-8 h-8 rounded-md text-white inline-flex items-center justify-center hover:opacity-90"
                        style={{ background: "#F59E0B" }}
                        data-testid="drill-duesoon">
                  <Maximize2 className="w-4 h-4" />
                </button>
              )}>
          {summary.due_soon.length === 0 ? <div className="text-sm text-slate-500 py-6 text-center">Nothing due soon.</div> : (
            <table className="hcd-table"><thead><tr><th>#</th><th>Vendor</th><th>Stage</th><th className="num">Days left</th></tr></thead>
              <tbody>
                {summary.due_soon.slice(0, 8).map((o, i) => (
                  <tr key={i} onClick={() => nav(`/contract/${o.id}`)} className="cursor-pointer hover:bg-slate-50">
                    <td className="font-mono text-xs">{o.contract_number}</td>
                    <td className="font-semibold">{o.vendor}</td>
                    <td>{o.stage}</td>
                    <td className="num text-amber-600 font-bold">{o.days_left}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      {/* Renewals */}
      <Card title="Upcoming Auto-Renewals (next 180 days)"
            actions={summary.renewals.length > 0 && (
              <button onClick={() => setDrill({ open: true, title: "Auto-Renewals", rows: summary.renewals })}
                      title="Pop out & message manager"
                      aria-label="Pop out auto-renewals and message manager"
                      className="w-8 h-8 rounded-md text-white inline-flex items-center justify-center hover:opacity-90"
                      style={{ background: "#005289" }}
                      data-testid="drill-renewals">
                <Maximize2 className="w-4 h-4" />
              </button>
            )}>
        {summary.renewals.length === 0 ? <div className="text-sm text-slate-500 py-6 text-center">No renewals in window.</div> : (
          <table className="hcd-table"><thead><tr><th>#</th><th>Vendor</th><th>Department</th><th className="num">Renewal</th><th className="num">Days</th><th className="num">Annual $</th></tr></thead>
            <tbody>
              {summary.renewals.map(r => (
                <tr key={r.contract_id} onClick={() => nav(`/contract/${r.contract_id}`)} className="cursor-pointer hover:bg-slate-50">
                  <td className="font-mono text-xs">{r.contract_number}</td>
                  <td className="font-semibold">{r.vendor}</td>
                  <td>{r.department}</td>
                  <td className="num text-xs">{new Date(r.renewal_date).toLocaleDateString()}</td>
                  <td className="num"><span className={`chip ${r.days_until <= 30 ? "chip-overdue" : r.days_until <= 60 ? "chip-duesoon" : "chip-ontrack"}`}><span className="dot" />{r.days_until}d</span></td>
                  <td className="num">{currency(r.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Priority flagged contracts */}
      <Card title="Priority Flagged Contracts" sub={`${priorities.length} contract${priorities.length===1?"":"s"} marked priority`}
            actions={priorities.length > 0 && (
              <button onClick={() => setDrill({ open: true, title: "Priority Flagged", rows: priorities.map(p => ({...p, days_left: p.days_until, id: p.contract_id})) })}
                      title="Pop out & message manager"
                      aria-label="Pop out priority contracts and message manager"
                      className="w-8 h-8 rounded-md text-white inline-flex items-center justify-center hover:opacity-90"
                      style={{ background: "#B91C1C" }}
                      data-testid="drill-priorities">
                <Maximize2 className="w-4 h-4" />
              </button>
            )}>
        {priorities.length === 0 ? <div className="text-sm text-slate-500 py-6 text-center">No priority contracts.</div> : (
          <table className="hcd-table" data-testid="priority-table">
            <thead><tr><th>#</th><th>Vendor</th><th>Department</th><th>Stage</th><th>Reason</th><th>Marked By</th><th className="num">Needed By</th><th className="num">Days</th></tr></thead>
            <tbody>
              {priorities.slice(0, 15).map(p => (
                <tr key={p.contract_id} onClick={() => nav(`/contract/${p.contract_id}`)} className="cursor-pointer hover:bg-slate-50">
                  <td className="font-mono text-xs">{p.contract_number}</td>
                  <td className="font-semibold flex items-center gap-1.5"><Flag className="w-3 h-3 text-red-600" />{p.vendor}</td>
                  <td className="text-slate-600 text-xs">{p.department}</td>
                  <td className="text-xs">{(p.stage || "").replace(/_/g," ")}</td>
                  <td className="text-xs text-slate-700 max-w-[280px] truncate" title={p.reason}>{p.reason}</td>
                  <td className="text-xs text-slate-500">{p.marked_by}</td>
                  <td className="num text-xs">{p.needed_by || "—"}</td>
                  <td className="num">
                    {p.days_until != null ? (
                      <span className={`chip ${p.days_until < 0 ? "chip-overdue" : p.days_until <= 7 ? "chip-duesoon" : "chip-ontrack"}`}>
                        <span className="dot" />{p.days_until < 0 ? `${-p.days_until}d over` : `${p.days_until}d`}
                      </span>
                    ) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title="Priority Update Feed" sub="Recent priority flags / clears">
        {priorityFeed.length === 0 ? <div className="text-sm text-slate-500 py-4 text-center">No recent priority activity.</div> : (
          <div className="divide-y divide-slate-100" data-testid="priority-feed">
            {priorityFeed.map((e, i) => (
              <div key={i} onClick={() => e.contract_id && nav(`/contract/${e.contract_id}`)}
                   className="py-2 px-1 hover:bg-slate-50 cursor-pointer flex items-start gap-3">
                <Flag className={`w-3.5 h-3.5 mt-0.5 ${e.action === "priority_set" ? "text-red-600" : "text-slate-400"}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-xs">
                    <span className="font-semibold">{e.actor}</span>{" "}
                    <span className="text-slate-500">{e.action === "priority_set" ? "flagged" : "cleared"}</span>{" "}
                    <span className="font-mono text-[10px] text-[#005289]">{e.contract_number}</span>
                    {e.needed_by && <span className="text-slate-500"> · needed by {e.needed_by}</span>}
                  </div>
                  {e.reason && <div className="text-[11px] text-slate-600 truncate">{e.reason}</div>}
                  <div className="text-[10px] text-slate-400">{new Date(e.at).toLocaleString()}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <DrillModal
        open={drill.open}
        title={drill.title}
        rows={drill.rows}
        onClose={() => setDrill({ ...drill, open: false })}
        onOpenContract={(id) => { nav(`/contract/${id}`); setDrill({ ...drill, open: false }); }}
        onMessageManager={messageManager}
      />
    </div>
  );
}
